import React, { useState, useEffect, useRef } from 'react';
import isEqual from 'lodash/isEqual';
import HelpIcon from './HelpIcon';
import './BankAccountsInput.css';

const BankAccountsInput = ({ value = [], onChange = () => {}, helptext }) => {
    const [accounts, setAccounts] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const prevAccounts = useRef();

    // Initialize from value on first load
    useEffect(() => {
        if (!loaded && Array.isArray(value)) {
            if (value.length > 0) {
                setAccounts(value);
            } else {
                // Initialize with one empty account
                setAccounts([createEmptyAccount()]);
            }
            prevAccounts.current = value;
            setLoaded(true);
        }
    }, [value, loaded]);

    // Update form state whenever local accounts array changes
    useEffect(() => {
        if (loaded && !isEqual(prevAccounts.current, accounts)) {
            prevAccounts.current = accounts;
            onChange(accounts);
        }
    }, [accounts, onChange, loaded]);

    // Sync external changes to local state
    useEffect(() => {
        if (loaded && !isEqual(value, prevAccounts.current)) {
            prevAccounts.current = value;
            setAccounts(Array.isArray(value) && value.length > 0 ? value : [createEmptyAccount()]);
        }
    }, [value, loaded]);

    function createEmptyAccount() {
        return {
            id: `account_${Date.now()}_${Math.random()}`,
            accountName: '',
            bankName: '',
            sortCode: '',
            accountNumber: '',
            people: []
        };
    }

    function createEmptyPerson() {
        return {
            id: `person_${Date.now()}_${Math.random()}`,
            name: '',
            email: '',
            isOnMandate: false,
            accessLevel: 'viewOnly'
        };
    }

    const addAccount = () => {
        setAccounts([...accounts, createEmptyAccount()]);
    };

    const removeAccount = (accountId) => {
        if (window.confirm("Are you sure you want to delete this bank account? Press OK to Confirm.")) {
            setAccounts(accounts.filter(acc => acc.id !== accountId));
        }
    };

    const updateAccountField = (accountId, field, newValue) => {
        setAccounts(accounts.map(acc =>
            acc.id === accountId ? { ...acc, [field]: newValue } : acc
        ));
    };

    const addPerson = (accountId) => {
        setAccounts(accounts.map(acc =>
            acc.id === accountId
                ? { ...acc, people: [...(acc.people || []), createEmptyPerson()] }
                : acc
        ));
    };

    const removePerson = (accountId, personId) => {
        if (window.confirm("Are you sure you want to remove this person? Press OK to Confirm.")) {
            setAccounts(accounts.map(acc =>
                acc.id === accountId
                    ? { ...acc, people: (acc.people || []).filter(p => p.id !== personId) }
                    : acc
            ));
        }
    };

    const updatePerson = (accountId, personId, field, newValue) => {
        setAccounts(accounts.map(acc =>
            acc.id === accountId
                ? {
                    ...acc,
                    people: (acc.people || []).map(p =>
                        p.id === personId ? { ...p, [field]: newValue } : p
                    )
                }
                : acc
        ));
    };

    const formatSortCode = (value) => {
        // Remove all non-digits
        const digits = value.replace(/\D/g, '');
        // Add hyphens after every 2 digits, max 6 digits
        const formatted = digits.slice(0, 6).replace(/(\d{2})(?=\d)/g, '$1-');
        return formatted;
    };

    const handleSortCodeChange = (accountId, value) => {
        const formatted = formatSortCode(value);
        updateAccountField(accountId, 'sortCode', formatted);
    };

    if (!loaded) return null;

    return (
        <fieldset className='bank-accounts-fieldset'>
            <legend className='bank-accounts-legend'>
                Bank Account Details
                <HelpIcon helptext={helptext} />
            </legend>

            <div className='bank-accounts-container'>
                {accounts.map((account, accountIndex) => (
                    <div key={account.id} className='bank-account-block'>
                        <div className='bank-account-header'>
                            <h4>Bank Account {accountIndex + 1}</h4>
                            {accounts.length > 1 && (
                                <button
                                    type='button'
                                    className='bank-account-remove-btn'
                                    onClick={() => removeAccount(account.id)}
                                >
                                    Remove Account
                                </button>
                            )}
                        </div>

                        <div className='bank-account-fields'>
                            <div className='bank-field'>
                                <label>
                                    Account Name <span className='required'>*</span>
                                </label>
                                <input
                                    type='text'
                                    value={account.accountName || ''}
                                    onChange={(e) => updateAccountField(account.id, 'accountName', e.target.value)}
                                    placeholder='e.g., Business Current Account'
                                />
                            </div>

                            <div className='bank-field'>
                                <label>
                                    Bank Name <span className='required'>*</span>
                                </label>
                                <input
                                    type='text'
                                    value={account.bankName || ''}
                                    onChange={(e) => updateAccountField(account.id, 'bankName', e.target.value)}
                                    placeholder='e.g., HSBC'
                                />
                            </div>

                            <div className='bank-field-row'>
                                <div className='bank-field bank-field-half'>
                                    <label>
                                        Sort Code <span className='required'>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        value={account.sortCode || ''}
                                        onChange={(e) => handleSortCodeChange(account.id, e.target.value)}
                                        placeholder='12-34-56'
                                        maxLength={8}
                                    />
                                </div>

                                <div className='bank-field bank-field-half'>
                                    <label>
                                        Account Number <span className='required'>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        value={account.accountNumber || ''}
                                        onChange={(e) => updateAccountField(account.id, 'accountNumber', e.target.value.replace(/\D/g, ''))}
                                        placeholder='12345678'
                                        maxLength={8}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* People Section */}
                        <div className='bank-people-section'>
                            <h5>People with Access</h5>

                            {(account.people || []).length === 0 && (
                                <p className='bank-no-people'>No people added yet</p>
                            )}

                            {(account.people || []).map((person, personIndex) => (
                                <div key={person.id} className='bank-person-block'>
                                    <div className='bank-person-header'>
                                        <span>Person {personIndex + 1}</span>
                                        <button
                                            type='button'
                                            className='bank-person-remove-btn'
                                            onClick={() => removePerson(account.id, person.id)}
                                        >
                                            ✕
                                        </button>
                                    </div>

                                    <div className='bank-person-fields'>
                                        <div className='bank-field'>
                                            <label>Name</label>
                                            <input
                                                type='text'
                                                value={person.name || ''}
                                                onChange={(e) => updatePerson(account.id, person.id, 'name', e.target.value)}
                                                placeholder='Full name'
                                            />
                                        </div>

                                        <div className='bank-field'>
                                            <label>Email</label>
                                            <input
                                                type='email'
                                                value={person.email || ''}
                                                onChange={(e) => updatePerson(account.id, person.id, 'email', e.target.value)}
                                                placeholder='email@example.com'
                                            />
                                        </div>

                                        <div className='bank-field-row'>
                                            <div className='bank-checkbox-field'>
                                                <label>
                                                    <input
                                                        type='checkbox'
                                                        checked={person.isOnMandate || false}
                                                        onChange={(e) => updatePerson(account.id, person.id, 'isOnMandate', e.target.checked)}
                                                    />
                                                    On Bank Mandate
                                                </label>
                                            </div>

                                            <div className='bank-field bank-field-half'>
                                                <label>Access Level</label>
                                                <select
                                                    value={person.accessLevel || 'viewOnly'}
                                                    onChange={(e) => updatePerson(account.id, person.id, 'accessLevel', e.target.value)}
                                                >
                                                    <option value='viewOnly'>View Only</option>
                                                    <option value='canPay'>Can Make Payments</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            <button
                                type='button'
                                className='bank-add-person-btn'
                                onClick={() => addPerson(account.id)}
                            >
                                + Add Person
                            </button>
                        </div>
                    </div>
                ))}

                <button
                    type='button'
                    className='bank-add-account-btn'
                    onClick={addAccount}
                >
                    + Add Another Bank Account
                </button>
            </div>
        </fieldset>
    );
};

export default BankAccountsInput;
